import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

type Ticket = {
  id: string;
  name: string;
  priceKopecks: number;
  capacity: number;
  remaining: number;
  isCustomAmount: boolean;
};

type EventData = {
  id: string;
  title: string;
  date: string;
  subtitle: string;
  tickets: Ticket[];
};

type PaidOrder = {
  id: string;
  fullName: string;
  eventTitle: string;
  ticketName: string;
  priceKopecks: number;
  paymentPurpose: string;
};

type PaymentState =
  | { kind: "closed" }
  | { kind: "processing" }
  | { kind: "success"; order: PaidOrder }
  | { kind: "error"; message: string };

const FUND_URL = "https://radostmoyaspb.ru/";
const LOGO_URL = "https://radostmoyaspb.ru/wp-content/uploads/2023/06/logo1-krysha_page-0001-1-scaled-e1687607229160.jpg";
const OFFER_URL = "https://radostmoyaspb.ru/donation-service-terms/";
const PRIVACY_URL = "https://radostmoyaspb.ru/wp-content/uploads/2023/07/politika-v-otnoshenii-obrabotki-personalnyh-dannyh-rm.pdf";

declare global {
  interface Window {
    __RADOST_TICKETS_API_BASE__?: string;
  }
}

const API_BASE = window.__RADOST_TICKETS_API_BASE__?.replace(/\/$/, "") ?? "";

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

function rubles(kopecks: number): string {
  return `${new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: kopecks % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(kopecks / 100)} ₽`;
}

function parseCustomAmount(value: string): number | null {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d{1,8}(?:\.\d{1,2})?$/.test(normalized)) return null;
  const kopecks = Math.round(Number(normalized) * 100);
  return Number.isSafeInteger(kopecks) && kopecks >= 100 && kopecks <= 1_000_000_000
    ? kopecks
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseEventResponse(value: unknown): EventData | null {
  if (!isRecord(value) || !isRecord(value.event)) return null;
  const event = value.event;
  if (
    typeof event.id !== "string" ||
    typeof event.title !== "string" ||
    typeof event.date !== "string" ||
    typeof event.subtitle !== "string" ||
    !Array.isArray(event.tickets)
  ) return null;

  const tickets: Ticket[] = [];
  for (const item of event.tickets) {
    if (
      !isRecord(item) ||
      typeof item.id !== "string" ||
      typeof item.name !== "string" ||
      typeof item.priceKopecks !== "number" ||
      typeof item.capacity !== "number" ||
      typeof item.remaining !== "number" ||
      typeof item.isCustomAmount !== "boolean"
    ) return null;
    tickets.push({
      id: item.id,
      name: item.name,
      priceKopecks: item.priceKopecks,
      capacity: item.capacity,
      remaining: item.remaining,
      isCustomAmount: item.isCustomAmount,
    });
  }

  return {
    id: event.id,
    title: event.title,
    date: event.date,
    subtitle: event.subtitle,
    tickets,
  };
}

function parsePaidOrder(value: unknown): PaidOrder | null {
  if (!isRecord(value) || value.ok !== true || !isRecord(value.order)) return null;
  const order = value.order;
  if (
    typeof order.id !== "string" ||
    typeof order.fullName !== "string" ||
    typeof order.eventTitle !== "string" ||
    typeof order.ticketName !== "string" ||
    typeof order.priceKopecks !== "number" ||
    typeof order.paymentPurpose !== "string"
  ) return null;
  return {
    id: order.id,
    fullName: order.fullName,
    eventTitle: order.eventTitle,
    ticketName: order.ticketName,
    priceKopecks: order.priceKopecks,
    paymentPurpose: order.paymentPurpose,
  };
}

function Logo({ footer = false }: { footer?: boolean }) {
  return (
    <a className={`brand${footer ? " brand--footer" : ""}`} href={FUND_URL} aria-label="Перейти на сайт фонда «Радость Моя»">
      <img className="brand__image" src={LOGO_URL} alt="Радость Моя" referrerPolicy="no-referrer" />
    </a>
  );
}

function PaymentDialog({ state, onClose }: { state: PaymentState; onClose: () => void }) {
  useEffect(() => {
    if (state.kind === "closed") return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state.kind !== "processing") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, state.kind]);

  if (state.kind === "closed") return null;
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="payment-modal" role="dialog" aria-modal="true" aria-labelledby="payment-title">
        {state.kind === "processing" && (
          <>
            <div className="payment-spinner" aria-hidden="true" />
            <span className="eyebrow">Тестовый платёж</span>
            <h2 id="payment-title">Подтверждаем оплату</h2>
            <p>Имитируем ответ банка и регистрируем участника. Обычно это занимает пару секунд.</p>
          </>
        )}
        {state.kind === "success" && (
          <>
            <div className="success-icon" aria-hidden="true">✓</div>
            <span className="eyebrow">Оплата прошла</span>
            <h2 id="payment-title">Спасибо, билет оформлен!</h2>
            <p>
              {state.order.fullName}, вы записаны на мероприятие «{state.order.eventTitle}».
            </p>
            <div className="receipt">
              <span>{state.order.ticketName}</span>
              <strong>{rubles(state.order.priceKopecks)}</strong>
              <small>{state.order.paymentPurpose}</small>
              <small>Номер заявки: {state.order.id.slice(0, 8).toUpperCase()}</small>
            </div>
            <button className="primary-button" type="button" onClick={onClose}>Готово</button>
          </>
        )}
        {state.kind === "error" && (
          <>
            <div className="error-icon" aria-hidden="true">!</div>
            <span className="eyebrow">Платёж не выполнен</span>
            <h2 id="payment-title">Попробуйте ещё раз</h2>
            <p>{state.message}</p>
            <button className="primary-button" type="button" onClick={onClose}>Вернуться</button>
          </>
        )}
      </section>
    </div>
  );
}

export default function App() {
  const [event, setEvent] = useState<EventData | null>(null);
  const [loadingError, setLoadingError] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [fullName, setFullName] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [consent, setConsent] = useState(false);
  const [paymentState, setPaymentState] = useState<PaymentState>({ kind: "closed" });

  const loadEvent = useCallback(async () => {
    const response = await fetch(apiUrl("/api/event"), { headers: { Accept: "application/json" } });
    const payload: unknown = await response.json();
    const parsed = response.ok ? parseEventResponse(payload) : null;
    if (!parsed) throw new Error("Не удалось загрузить мероприятие");
    setEvent(parsed);
    setSelectedTicketId((current) => {
      if (parsed.tickets.some((ticket) => ticket.id === current && (ticket.isCustomAmount || ticket.remaining > 0))) return current;
      return parsed.tickets.find((ticket) => ticket.remaining > 0 || ticket.isCustomAmount)?.id ?? "";
    });
  }, []);

  useEffect(() => {
    loadEvent().catch(() => setLoadingError("Страница временно недоступна. Обновите её через минуту."));
  }, [loadEvent]);

  const selectedTicket = useMemo(
    () => event?.tickets.find((ticket) => ticket.id === selectedTicketId) ?? null,
    [event, selectedTicketId],
  );
  const normalizedName = fullName.trim().replace(/\s+/g, " ");
  const nameIsValid = normalizedName.length >= 5 && normalizedName.split(" ").length >= 2;
  const customAmountKopecks = parseCustomAmount(customAmount);
  const payableAmount = selectedTicket?.isCustomAmount ? customAmountKopecks : selectedTicket?.priceKopecks ?? null;
  const ticketIsAvailable = Boolean(selectedTicket && (selectedTicket.isCustomAmount || selectedTicket.remaining > 0));
  const canPay = Boolean(ticketIsAvailable && payableAmount !== null && nameIsValid && consent);

  const submitPayment = async (formEvent: FormEvent) => {
    formEvent.preventDefault();
    if (!canPay || !selectedTicket) return;
    setPaymentState({ kind: "processing" });

    try {
      const responsePromise = fetch(apiUrl("/api/checkout"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ticketId: selectedTicket.id,
          fullName: normalizedName,
          consent: true,
          customAmountKopecks: selectedTicket.isCustomAmount ? customAmountKopecks : null,
        }),
      });
      const [response] = await Promise.all([
        responsePromise,
        new Promise((resolve) => window.setTimeout(resolve, 900)),
      ]);
      const payload: unknown = await response.json();
      const order = response.ok ? parsePaidOrder(payload) : null;
      if (!order) {
        const unavailable = response.status === 409;
        throw new Error(
          unavailable
            ? "Этот билет только что закончился. Выберите другой вариант."
            : "Не удалось подтвердить тестовую оплату.",
        );
      }
      setPaymentState({ kind: "success", order });
      setFullName("");
      setCustomAmount("");
      setConsent(false);
      await loadEvent();
    } catch (error) {
      setPaymentState({
        kind: "error",
        message: error instanceof Error ? error.message : "Произошла неизвестная ошибка.",
      });
      await loadEvent().catch(() => undefined);
    }
  };

  const closePayment = useCallback(() => setPaymentState({ kind: "closed" }), []);
  return (
    <div className="site-shell">
      <header>
        <div className="top-strip">
          <div className="container top-strip__inner">
            <span>Санкт-Петербург</span>
            <span className="top-strip__divider" aria-hidden="true" />
            <span>Место заботы и поддержки</span>
          </div>
        </div>
        <div className="brand-row container">
          <Logo />
          <div className="brand-row__aside">
            <span className="test-pill">Тестовый режим оплаты</span>
            <a href="#tickets" className="header-action">Выбрать билет</a>
          </div>
        </div>
        <nav className="nav-band" aria-label="Навигация по странице">
          <div className="container nav-band__inner">
            <a href="#event">Мероприятие</a>
            <a href="#tickets">Билеты</a>
          </div>
        </nav>
      </header>

      <main id="event">
        {loadingError ? (
          <section className="load-state container" role="alert">
            <h1>Не удалось открыть мероприятие</h1>
            <p>{loadingError}</p>
            <button className="primary-button" type="button" onClick={() => window.location.reload()}>Обновить страницу</button>
          </section>
        ) : !event ? (
          <section className="load-state container" aria-live="polite">
            <div className="payment-spinner" aria-hidden="true" />
            <p>Загружаем мероприятие…</p>
          </section>
        ) : (
          <>
            <section className="hero">
              <div className="hero__orb hero__orb--one" aria-hidden="true" />
              <div className="hero__orb hero__orb--two" aria-hidden="true" />
              <div className="container hero__grid">
                <div className="hero__copy">
                  <span className="eyebrow">Поддержите мероприятие</span>
                  <h1>{event.title}</h1>
                  <div className="date-line">
                    <span className="date-line__icon" aria-hidden="true">⌁</span>
                    <strong>{event.date}</strong>
                  </div>
                  <div className="pink-rule" aria-hidden="true" />
                  <p className="hero__subtitle">{event.subtitle}</p>
                  <p className="entry-note"><span aria-hidden="true">✓</span> При входе на мероприятие просто назовите ваше ФИО — электронный билет не нужен!</p>
                </div>

                <div className="purchase-wrap" id="tickets">
                  <div className="purchase-card">
                    <div className="purchase-card__heading">
                      <span className="step-number">01</span>
                      <div>
                        <span className="eyebrow">Регистрация</span>
                        <h2>Выберите билет</h2>
                      </div>
                    </div>

                    <form onSubmit={submitPayment} noValidate>
                      <label className="field-label" htmlFor="ticket">Вариант билета</label>
                      <div className="select-wrap">
                        <select
                          id="ticket"
                          value={selectedTicketId}
                          onChange={(event) => setSelectedTicketId(event.target.value)}
                          disabled={event.tickets.length === 0}
                        >
                          {event.tickets.map((ticket) => (
                            <option key={ticket.id} value={ticket.id} disabled={!ticket.isCustomAmount && ticket.remaining === 0}>
                              {ticket.name}{ticket.isCustomAmount ? "" : ` — ${rubles(ticket.priceKopecks)}${ticket.remaining === 0 ? " — мест нет" : ""}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedTicket && !selectedTicket.isCustomAmount && (
                        <div className="ticket-meta" aria-live="polite">
                          <strong>{rubles(selectedTicket.priceKopecks)}</strong>
                          <span>
                            {selectedTicket.remaining > 0
                              ? `Осталось мест: ${selectedTicket.remaining}`
                              : "Все места заняты"}
                          </span>
                        </div>
                      )}

                      {selectedTicket?.isCustomAmount && (
                        <div className="custom-amount-field">
                          <label className="field-label" htmlFor="custom-amount">Сумма пожертвования, ₽</label>
                          <div className="amount-input-wrap">
                            <input
                              id="custom-amount"
                              name="customAmount"
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              placeholder="Например, 1500"
                              value={customAmount}
                              maxLength={11}
                              onChange={(event) => setCustomAmount(event.target.value)}
                            />
                            <span aria-hidden="true">₽</span>
                          </div>
                        </div>
                      )}

                      <label className="field-label" htmlFor="full-name">ФИО участника</label>
                      <input
                        id="full-name"
                        name="fullName"
                        type="text"
                        autoComplete="name"
                        placeholder="Иванов Иван Иванович"
                        value={fullName}
                        maxLength={120}
                        onChange={(event) => setFullName(event.target.value)}
                      />

                      <div className="consent-row">
                        <input
                          id="consent"
                          type="checkbox"
                          checked={consent}
                          onChange={(event) => setConsent(event.target.checked)}
                        />
                        <label htmlFor="consent">
                          Я принимаю условия {" "}
                          <a href={OFFER_URL} target="_blank" rel="noreferrer">оферты</a>
                          {" "}и {" "}
                          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">политики конфиденциальности</a>
                        </label>
                      </div>

                      <button className="pay-button" type="submit" disabled={!canPay}>
                        <span>Оплатить</span>
                        <strong>{payableAmount === null ? "—" : rubles(payableAmount)}</strong>
                      </button>
                      <p className="prototype-note">Сейчас кнопка имитирует успешную оплату без списания денег</p>
                    </form>
                  </div>
                </div>
              </div>
            </section>

            <div className="after-hero-space" aria-hidden="true" />
          </>
        )}
      </main>

      <footer>
        <div className="container footer__inner">
          <Logo footer />
          <div className="footer__details">
            <a className="footer__phone" href="https://t.me/79006280807">+7 (900) 628-08-07</a>
            <p>Социальный центр помощи семье и детям «Радость Моя» имени преподобного Серафима Саровского © 2022 - 2026 · Все права защищены</p>
          </div>
        </div>
      </footer>

      <PaymentDialog state={paymentState} onClose={closePayment} />
    </div>
  );
}
